import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { extractExpirationInfo } from 'lib/util'
import { Badge, Row, Col } from 'reactstrap'
import ErrorBoundary from 'components/ErrorBoundary'
import FieldsetItem from 'components/FieldsetItem'
import FieldsetItemLarge from 'components/FieldsetItemLarge'
import { fetchSetupAdminUserId } from 'store/css-admin/userManager/operations'
import IsActiveBadge from './IsActiveBadge'
import {
  NetSuiteAsset,
  NetSuiteAssetProduct,
  NetSuiteAssetSite,
  RootState,
} from 'types'

type LicenseSummaryProps = {
  netSuiteAsset?: NetSuiteAsset
}

type UserManagerSlice = { setupAdminUserId?: number } // TODO: add user Slice to types later

const LicenseSummary: React.FC<LicenseSummaryProps> = ({ netSuiteAsset }) => {
  const dispatch = useDispatch()
  const setupAdminUserId = useSelector(
    (state: RootState) =>
      (state.userManager as unknown as UserManagerSlice)?.setupAdminUserId,
  )
  const {
    adminEmail,
    cancelledDate,
    contractLineNSId,
    id,
    isActive,
    isInternal,
    managerNetSuiteCustomerId,
    modifiedDate,
    product,
    site,
  } = netSuiteAsset || ({} as NetSuiteAsset)
  useEffect(() => {
    if (adminEmail) {
      dispatch(fetchSetupAdminUserId(adminEmail))
    }
  }, [adminEmail, dispatch])

  // TODO: add type for extractExpirationInfo
  const dateInfo: any = extractExpirationInfo(netSuiteAsset, 'YYYY-MM-DD')

  const {
    name: siteName,
    id: siteId,
    nsCustomerId,
  } = site || ({} as NetSuiteAssetSite)
  const { description } = product || ({} as NetSuiteAssetProduct)

  const setupAdminEmailLink =
    !!adminEmail && setupAdminUserId
      ? `/css-admin/users/${adminEmail}/${setupAdminUserId}`
      : null

  return (
    <ErrorBoundary>
      <fieldset>
        <legend>Details</legend>
        <Row>
          <Col sm={4}>
            <FieldsetItem
              data={contractLineNSId}
              title="Contract Line Id"
              url={`/css-admin/contractLine/${contractLineNSId}`}
            />
            <FieldsetItem
              data={managerNetSuiteCustomerId}
              title="Purchaser NS Customer Id"
            />
            <FieldsetItem data={id} title="Internal Asset Id" />

            <FieldsetItem
              data={<IsActiveBadge isActive={isInternal} />}
              title="Is Internal"
            />

            <FieldsetItem
              data={<IsActiveBadge isActive={isActive} />}
              title="Is Active"
            />
          </Col>
          <Col sm={5}>
            <FieldsetItemLarge data={description} title="Product" />
            <FieldsetItemLarge
              data={siteName}
              title="Assigned Site Name"
              url={`/css-admin/sites/${siteId}`}
            />

            <FieldsetItem
              data={nsCustomerId}
              title="Assigned Site NS Customer ID"
              url={`/css-admin/sites/${siteId}`}
            />

            <FieldsetItem
              data={adminEmail || <Badge color="warning">None</Badge>}
              title="Setup Admin Email"
              url={setupAdminEmailLink}
            />
          </Col>
          <Col sm={3}>
            <FieldsetItem
              color="none"
              data={dateInfo.originationDate.formatted}
              title="Origination Date"
            />
            <FieldsetItem
              color="none"
              data={dateInfo.startDate.formatted}
              title="Start Date"
            />
            <FieldsetItem
              color={dateInfo.isExpired ? 'danger' : 'none'}
              data={dateInfo.expirationDate.formatted}
              title="Expiration"
            />
            {cancelledDate && (
              <FieldsetItem
                color="warning"
                data={dateInfo.cancelledDate.formatted}
                title="Cancellation"
              />
            )}
            {modifiedDate && (
              <FieldsetItem
                color="none"
                data={moment(modifiedDate).format('YYYY-MM-DD')}
                title="Modified Date"
              />
            )}
          </Col>
        </Row>
      </fieldset>
    </ErrorBoundary>
  )
}

export default LicenseSummary
